import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderKanban, Clock, Ticket, CheckSquare } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { parseApiDate } from '../lib/utils';

const STATUS_META = {
  planning:    { label: 'Planning',    color: '#6366f1' },
  in_progress: { label: 'In Progress', color: '#10b981' },
  on_hold:     { label: 'On Hold',     color: '#f59e0b' },
  completed:   { label: 'Completed',   color: '#64748b' },
  cancelled:   { label: 'Cancelled',   color: '#ef4444' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#888' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: 99, fontSize: 12, fontWeight: 500,
      background: m.color + '1a', color: m.color, border: `1px solid ${m.color}33`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

function MiniProgress({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 99 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 99, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{done}/{total}</span>
    </div>
  );
}

export default function ProjectsPage() {
  const { isStaff } = useAuth();
  const navigate    = useNavigate();
  const [projects, setProjects] = useState([]);
  const [orgs,     setOrgs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOrg,    setFilterOrg]    = useState('');

  useEffect(() => {
    if (isStaff) api.get('/orgs').then(d => setOrgs(d.orgs || [])).catch(() => {});
  }, [isStaff]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filterStatus) qs.set('status', filterStatus);
    if (filterOrg)    qs.set('org_id', filterOrg);
    if (search)       qs.set('search', search);
    api.get(`/projects?${qs}`).then(d => setProjects(d.projects || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filterStatus, filterOrg, search]);

  const activeProjects = projects.filter(p => !['completed','cancelled'].includes(p.status));
  const doneProjects   = projects.filter(p => ['completed','cancelled'].includes(p.status));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h2>Projects</h2>
        {isStaff && (
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/projects/new')}>
            <Plus size={14} /> New Project
          </button>
        )}
      </div>

      <div className="page-body">
        {/* Filters */}
        <div className="flex gap-2 mb-5" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 200px' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Search projects…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ flex: '0 0 160px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select>
          {isStaff && orgs.length > 0 && (
            <select className="form-select" style={{ flex: '0 0 180px' }} value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
              <option value="">All organizations</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 64, color: 'var(--text-muted)' }}>
            <FolderKanban size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <p>No projects found</p>
          </div>
        ) : (
          <>
            {/* Active projects */}
            {activeProjects.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
                  Active — {activeProjects.length}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                  {activeProjects.map(p => <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />)}
                </div>
              </div>
            )}

            {/* Completed/cancelled */}
            {doneProjects.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
                  Completed / Cancelled — {doneProjects.length}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                  {doneProjects.map(p => <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ project: p, onClick }) {
  const hours = p.total_minutes > 0 ? (p.total_minutes / 60).toFixed(1) : null;
  const estH  = p.estimated_hours;
  return (
    <div className="card" style={{ padding: '16px 18px', cursor: 'pointer', transition: 'box-shadow .15s' }}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
      <div className="flex justify-between items-start" style={{ marginBottom: 8, gap: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{p.title}</h3>
        <StatusBadge status={p.status} />
      </div>

      {p.org && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{p.org.name}</div>}

      {p.description && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {p.description}
        </p>
      )}

      {/* Milestone progress */}
      {p.milestone_count > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Milestones</div>
          <MiniProgress done={p.milestones_done} total={p.milestone_count} />
        </div>
      )}

      <div className="flex gap-3" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {hours && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} />
            {hours}h{estH ? ` / ${estH}h` : ''}
          </span>
        )}
        {p.ticket_count > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Ticket size={12} />{p.ticket_count} ticket{p.ticket_count !== 1 ? 's' : ''}
          </span>
        )}
        {p.due_date && (
          <span style={{ marginLeft: 'auto' }}>
            Due {parseApiDate(m.due_date).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}
